export type Volume = {
  n: number;
  emoji: string;
  title: string;
  subtitle: string;
  desc: string;
  detail: string;
  includes: string[];
  objective: string;
};

export type Bonus = {
  n: string;
  title: string;
  value: string;
  subtitle: string;
  desc: string;
  includes: string[];
  why: string;
  cover: string;
};

export const pillars = [
  'Security Reports',
  'Patrol & Site Operations',
  'Shift Handovers',
  'Risk Assessment',
  'Client Communication',
  'Supervision',
];

export const volumes: Volume[] = [
  {
    n: 1,
    emoji: '📘',
    title: 'Security Report Writing',
    subtitle: 'The Professional Documentation Framework',
    desc: 'Create a more structured starting point for incident reports, daily activity reports, patrol observations, and security logs.',
    detail:
      'Learn how to guide AI to organize the information you provide into clear, chronological, professional drafts without inventing facts or changing the meaning of your observations.',
    includes: [
      'Incident report drafting prompts',
      'Daily activity and security log structures',
      'Patrol observation documentation',
      'Chronological incident narratives',
      'Missing-information identification',
      'Report clarity and consistency reviews',
      'Structured fields for dates, times, locations, observations, and actions taken',
    ],
    objective: 'Spend less effort organizing the format and more attention verifying the accuracy of the information.',
  },
  {
    n: 2,
    emoji: '📗',
    title: 'Patrol & Site Operations',
    subtitle: 'The Daily Operations Organizer',
    desc: 'Organize recurring site activities, patrol observations, inspection records, and operational checklists using structured AI instructions.',
    detail:
      'Whether you work in commercial buildings, residential properties, warehouses, offices, or other security environments, this volume helps you develop reusable starting points that can be adapted to the requirements of each site.',
    includes: [
      'Patrol activity summaries',
      'Site inspection checklists',
      'Access observation records',
      'Opening and closing procedure documentation',
      'Property and equipment observation logs',
      'Routine operational task lists',
      'Site-specific reporting templates',
    ],
    objective:
      'Create a more consistent approach to documenting routine activities and organizing site-related responsibilities.',
  },
  {
    n: 3,
    emoji: '📙',
    title: 'Shift Management & Handovers',
    subtitle: 'The Continuity & Accountability Framework',
    desc: 'A shift handover should communicate what happened, what remains pending, and what requires attention next.',
    detail:
      'This volume helps you structure handover notes and shift summaries so that relevant information can be organized for the incoming team.',
    includes: [
      'Shift handover report prompts',
      'Pending-task summaries',
      'Outstanding incident follow-ups',
      'Equipment and key-control record templates',
      'Shift briefing structures',
      'Supervisor update drafts',
      'End-of-shift summaries and activity reviews',
    ],
    objective:
      'Make shift-related information easier to organize, review, and communicate between authorized personnel.',
  },
  {
    n: 4,
    emoji: '📕',
    title: 'Risk Assessment & Prevention',
    subtitle: 'The Site Observation & Risk Review Framework',
    desc: 'Organize observations about potential hazards, site conditions, access concerns, and operational weaknesses into structured drafts for further assessment.',
    detail:
      'AI can help organize information you provide, but it does not replace a qualified risk assessment, site inspection, or professional judgment.',
    includes: [
      'Site observation and hazard documentation',
      'Risk identification worksheets',
      'Environmental and operational concern summaries',
      'Preventive action planning templates',
      'Inspection follow-up records',
      'Risk review checklists',
      'Structured recommendations for professional assessment',
    ],
    objective:
      'Make it easier to record observations systematically and prepare information for review by the appropriate personnel.',
  },
  {
    n: 5,
    emoji: '📓',
    title: 'Communication & Client Service',
    subtitle: 'The Professional Communication Framework',
    desc: 'Professional communication matters when providing updates, documenting concerns, responding to client requests, or coordinating with colleagues.',
    detail:
      'This volume provides reusable prompts for drafting clear, courteous, and task-appropriate communications.',
    includes: [
      'Client update messages',
      'Incident notification drafts',
      'Professional email templates',
      'Service issue summaries',
      'De-escalation communication examples',
      'Internal coordination messages',
      'Follow-up and escalation templates',
    ],
    objective:
      'Establish a consistent starting point for professional communication while allowing you to adapt the tone and details to each situation.',
  },
  {
    n: 6,
    emoji: '📔',
    title: 'Supervision, Training & Compliance',
    subtitle: 'The Management & Quality Framework',
    desc: 'Supervisors and security managers frequently need to organize instructions, review documentation, prepare briefings, and track operational improvements.',
    detail: 'This volume helps structure those recurring administrative tasks with customizable AI prompts.',
    includes: [
      'Supervisor briefing templates',
      'Staff task assignment structures',
      'Training scenario development prompts',
      'Documentation review checklists',
      'Internal memo and administrative report drafts',
      'Performance feedback frameworks',
      'Corrective-action and follow-up records',
    ],
    objective:
      'Support more structured supervision, documentation review, and professional development activities.',
  },
];

export const volumeFeatures = [
  {
    title: 'Ready-to-Customize AI Prompts',
    desc: 'Structured instructions you can copy, adapt, and use as a starting point for a specific task.',
  },
  {
    title: 'Fill-in-the-Blank Fields',
    desc: 'Clearly marked fields for dates, times, locations, observations, actions taken, and other relevant information.',
  },
  {
    title: 'Reusable Frameworks',
    desc: 'Prompt structures you can adapt for recurring tasks instead of repeatedly building instructions from scratch.',
  },
  {
    title: 'Review & Verification Checklists',
    desc: 'Reminders to check completeness, chronology, clarity, consistency, and missing information before using a draft.',
  },
  {
    title: 'Organized Professional Categories',
    desc: 'Prompts grouped by task so you can identify a relevant starting point more easily.',
  },
];

export const bonuses: Bonus[] = [
  {
    n: '01',
    title: 'The Security Report Template Vault™',
    value: '$32',
    subtitle: 'Organize Reports, Incident Records & Professional Documentation',
    desc: 'A complementary resource focused on reusable documentation structures for common private security tasks.',
    includes: [
      'Incident report template structures',
      'Daily activity record layouts',
      'Observation and patrol log formats',
      'Documentation field checklists',
      'Report organization frameworks',
      'Structured starting points for recurring records',
    ],
    why: 'Having a reusable structure can make it easier to organize the information you need before preparing a report.',
    cover: 'https://cdn.magicpatterns.com/uploads/ozaXrLTVLiPuiSVJa9CnCF/Plantilla_de_Seguridad_en_Control_Room.webp',
  },
  {
    n: '02',
    title: 'The Shift Handover Toolkit™',
    value: '$29',
    subtitle: 'Make Shift Transitions More Structured',
    desc: 'A practical toolkit for organizing the information that needs to be communicated when responsibilities pass from one shift to another.',
    includes: [
      'Shift handover checklist structures',
      'Outstanding-task tracking templates',
      'Shift briefing frameworks',
      'Pending incident follow-up records',
      'Equipment and key-control handover fields',
      'Incoming-shift priority summaries',
    ],
    why: 'A consistent handover format helps teams identify what has been completed, what remains pending, and what requires follow-up.',
    cover: 'https://cdn.magicpatterns.com/uploads/xodBEkKfK33grL8ptnF2Fv/Plantilla_de_Seguridad_en_Control_Room_(1).webp',
  },
  {
    n: '03',
    title: 'The Professional Security Communication Kit™',
    value: '$32',
    subtitle: 'Communicate More Clearly With Clients, Colleagues & Supervisors',
    desc: 'A complementary collection of communication frameworks for recurring professional situations.',
    includes: [
      'Client update message templates',
      'Professional incident notification structures',
      'Internal email and memo prompts',
      'Service issue communication examples',
      'Follow-up message frameworks',
      'Calm, professional wording for sensitive conversations',
    ],
    why: "Reusable communication structures help you prepare clearer messages while adapting the wording to the circumstances and your organization's policies.",
    cover: 'https://cdn.magicpatterns.com/uploads/3s3zhbet2h1UuMinKGy9Y5/Plantilla_de_Seguridad_en_Control_Room_(2).webp',
  },
  {
    n: '04',
    title: 'The AI Workflow Quickstart Guide',
    value: '$27',
    subtitle: 'Learn to Use AI Prompts More Effectively',
    desc: 'A practical introduction to using the prompts, reviewing AI-generated content, and building repeatable workflows.',
    includes: [
      'How to use and customize a prompt',
      'How to provide relevant task context',
      'How to request a specific document structure',
      'How to identify missing information',
      'How to verify facts and review generated drafts',
      'Privacy-conscious AI workflow practices',
      'How to adapt prompts to different compatible AI assistants',
    ],
    why: 'The quality of an AI-generated draft depends on the information supplied, the instructions provided, and the human review performed afterward.',
    cover: 'https://cdn.magicpatterns.com/uploads/rfakrZ7MaSy25ATBj9crax/Plantilla_de_Seguridad_en_Control_Room_(3).webp',
  },
];
